/**
 * WatchReelsPage — public gallery of FreightPilotAI promo reels.
 *
 * Route: /watch
 *
 * Anyone can browse every promo video hosted on Cloudinary CDN. Each
 * card shows the vertical video (auto-loop on hover), the topic label,
 * duration, and a big "Sign up free" CTA with UTM tags that bucket
 * every click into the `promo_gallery` campaign.
 *
 * Backend: GET /api/reels → { reels: [...], count }
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, Clock, Copy, ShareNetwork, LinkSimple } from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function fmtDuration(sec) {
  if (!sec) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Fire-and-forget click counter. Uses `keepalive` so the request
 *  completes even after the browser starts navigating away. */
function trackReelClick(reelId, source) {
  try {
    fetch(`${API_BASE}/api/reels/track-click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reel_id: reelId, source }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* noop */ }
}

/** Stable per-session viewer key so the backend can dedupe repeated
 *  progress hits from the same tab. Persists in sessionStorage — one
 *  new key per tab / per full reload. */
function getViewerKey() {
  try {
    let k = sessionStorage.getItem("fp_reel_viewer");
    if (!k) {
      k = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
      sessionStorage.setItem("fp_reel_viewer", k);
    }
    return k;
  } catch {
    return "anon";
  }
}

/** Send max-progress pct to the backend. Called from a throttled
 *  onTimeUpdate handler so we only ping every ~2s per reel. */
function trackReelProgress(reelId, pct) {
  try {
    fetch(`${API_BASE}/api/reels/track-progress`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        reel_id: reelId,
        viewer_key: getViewerKey(),
        pct_reached: Math.max(0, Math.min(100, pct)),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* noop */ }
}

/** Build a throttled timeupdate handler bound to a specific reel.
 *  Only pings the backend when the max % watched grows by ≥5. */
function makeProgressHandler(reelId) {
  let lastSent = 0;
  return (e) => {
    const v = e.target;
    if (!v || !v.duration || v.duration === Infinity) return;
    const pct = (v.currentTime / v.duration) * 100;
    if (pct - lastSent >= 5 || (pct >= 95 && lastSent < 95)) {
      lastSent = pct;
      trackReelProgress(reelId, pct);
    }
  };
}

export default function WatchReelsPage() {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [cap, setCap] = useState({ id: null, text: "" });
  const [capStyle, setCapStyle] = useState(() => {
    try { return localStorage.getItem("fp_cap_style") || "white"; } catch { return "white"; }
  });
  const videoRefs = useRef({});

  const pickCapStyle = (s) => {
    setCapStyle(s);
    try { localStorage.setItem("fp_cap_style", s); } catch { /* noop */ }
  };

  // Bold sound-off captions — track the active SRT segment while a reel plays.
  const updateCaption = (reel, e) => {
    if (!reel.captions || !reel.captions.length) return;
    const t = e.target.currentTime;
    const seg = reel.captions.find((c) => t >= c.start && t < c.end);
    const text = seg ? seg.text : "";
    setCap((p) => (p.id === reel.id && p.text === text ? p : { id: reel.id, text }));
  };

  // TikTok-style flow: when a reel ends, glide to the next one and play it.
  const handleEnded = (reelId) => {
    const idx = reels.findIndex((r) => r.id === reelId);
    if (idx < 0 || reels.length < 2) return;
    const next = reels[(idx + 1) % reels.length];
    const card = document.getElementById(next.id);
    if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
    const el = videoRefs.current[next.id];
    if (el) el.play().catch(() => {});
  };

  // Only one reel plays at a time — starting one pauses the rest.
  const handlePlay = (reelId) => {
    Object.entries(videoRefs.current).forEach(([id, el]) => {
      if (id !== reelId && el && !el.paused) el.pause();
    });
  };

  useEffect(() => {
    document.title = "FreightPilotAI — Watch Reels";
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/reels?limit=24`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setReels(data.reels || []);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const copyShare = async (reel) => {
    const share = `${window.location.origin}/watch#${reel.id}`;
    try {
      await navigator.clipboard.writeText(share);
      toast.success("Reel link copied — paste anywhere");
    } catch {
      toast.error("Couldn't copy — check clipboard permissions");
    }
  };

  /** Build the full TikTok bio-link menu — a paste-ready one-liner
   *  containing every promo topic's UTM-tagged signup link with a
   *  short human label. Perfect for TikTok's single-URL bio slot
   *  when combined with a link-in-bio service like Beacons/Linktree,
   *  OR when the founder wants to hand-craft his own menu. */
  const bioLinkMenu = useMemo(() => {
    if (!reels.length) return "";
    // De-dupe by topic — take the highest-view (first) reel per topic
    const byTopic = {};
    for (const r of reels) {
      if (r.topic && !byTopic[r.topic]) byTopic[r.topic] = r;
    }
    const lines = Object.values(byTopic).map(
      (r) => `→ ${r.topic_label}: ${r.signup_url}`
    );
    return (
      `📌 FreightPilotAI — pick what matters most to you:\n\n` +
      lines.join("\n") +
      `\n\nAll options: https://freightpilotai.ai/watch`
    );
  }, [reels]);

  const copyBioMenu = async () => {
    if (!bioLinkMenu) {
      toast.error("No reels loaded yet");
      return;
    }
    try {
      await navigator.clipboard.writeText(bioLinkMenu);
      toast.success("Bio-link menu copied — paste into TikTok bio or Beacons");
    } catch {
      toast.error("Couldn't copy — check clipboard permissions");
    }
  };

  // Memoize one progress handler per reel so `lastSent` state survives
  // re-renders (otherwise every timeupdate re-triggers the ping).
  const progressHandlers = useMemo(() => {
    const map = {};
    for (const r of reels) map[r.id] = makeProgressHandler(r.id);
    return map;
  }, [reels]);

  const champs = useMemo(() => reels.filter((r) => r.kind === "champion_cameo"), [reels]);
  const promos = useMemo(() => reels.filter((r) => r.kind !== "champion_cameo"), [reels]);

  // Shared card renderer — used by both the champion wall and the main grid.
  const renderReelCard = (reel, extraClass = "") => (
    <article
      key={reel.id}
      id={reel.id}
      data-testid={`watch-reels-card-${reel.id}`}
      className={`group rounded-xl overflow-hidden bg-black border border-white/10 hover:border-[#FF5A1F]/60 transition-colors flex flex-col ${extraClass}`}
    >
      <div className="relative aspect-[9/16] bg-black overflow-hidden">
        <video
          src={reel.cdn_url}
          controls
          playsInline
          preload="metadata"
          ref={(el) => { if (el) videoRefs.current[reel.id] = el; }}
          onTimeUpdate={(e) => { progressHandlers[reel.id]?.(e); updateCaption(reel, e); }}
          onEnded={() => handleEnded(reel.id)}
          onPlay={() => handlePlay(reel.id)}
          data-testid={`watch-reels-player-${reel.id}`}
          className="w-full h-full object-cover"
        />
        {cap.id === reel.id && cap.text && (
          <div
            data-testid={`watch-reels-caption-${reel.id}`}
            className="absolute inset-x-1.5 bottom-12 flex justify-center pointer-events-none z-10"
          >
            <span className={`rounded-md px-2 py-1 text-center text-[12px] font-black leading-snug shadow-lg ${
              capStyle === "orange"
                ? "bg-[#FF5A1F] text-black"
                : "bg-black/80 backdrop-blur text-white"
            }`}>
              {cap.text}
            </span>
          </div>
        )}
        <div className="absolute top-2 left-2 rounded-md bg-black/70 backdrop-blur px-1.5 py-0.5 text-[10px] font-mono text-white flex items-center gap-1">
          <Clock size={10} weight="bold" /> {fmtDuration(reel.duration_sec)}
        </div>
        {reel.kind === "champion_cameo" && (
          <div className="absolute top-2 right-2 rounded-md bg-[#FF5A1F] px-1.5 py-0.5 text-[9px] font-black text-black tracking-wide"
               data-testid={`watch-reels-champ-tag-${reel.id}`}>
            CHAMPION
          </div>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col gap-2">
        <div className="font-bold text-sm text-white truncate" title={reel.topic_label}>
          {reel.topic_label}
        </div>
        <div className="flex gap-1.5 mt-auto pt-1">
          <a
            href={reel.signup_url}
            onClick={() => trackReelClick(reel.id, "card_signup")}
            data-testid={`watch-reels-signup-${reel.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-bold bg-[#FF5A1F] hover:bg-[#FF6A35] text-black transition-colors"
          >
            Sign up free <ArrowRight size={10} weight="bold" />
          </a>
          <button
            type="button"
            onClick={() => copyShare(reel)}
            data-testid={`watch-reels-share-${reel.id}`}
            title="Copy shareable link"
            className="inline-flex items-center justify-center rounded-md px-2 py-1.5 text-[11px] font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
          >
            <ShareNetwork size={12} weight="bold" />
          </button>
        </div>
      </div>
    </article>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="watch-reels-page">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-black/60 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="watch-reels-logo">
            <Logo className="h-7 w-7" />
          </Link>
          <Link
            to="/?utm_source=reels_page&utm_medium=organic&utm_campaign=promo_gallery&utm_content=header"
            data-testid="watch-reels-header-cta"
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold bg-[#FF5A1F] hover:bg-[#FF6A35] text-black transition-colors"
          >
            Sign up free <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-6">
        <div className="text-[11px] uppercase tracking-widest text-[#FF9764] font-bold mb-2">
          Founder-Led · Real US Trucking SaaS
        </div>
        <h1 className="text-4xl sm:text-5xl font-black leading-tight max-w-2xl">
          Watch how FreightPilotAI gives owner-ops their weekends back.
        </h1>
        <p className="text-white/70 mt-3 max-w-xl text-sm sm:text-base">
          Every short is straight from Mike — the owner-op who ran 6 trucks himself and built the tool he wished he had. Start your 7-day free trial in under a minute.
        </p>
        {reels.length > 0 && (
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={copyBioMenu}
              data-testid="watch-reels-bio-link-hero"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold bg-white/5 hover:bg-white/10 text-white border border-[#FF5A1F]/40 hover:border-[#FF5A1F] transition-colors"
              title="Copies a paste-ready menu with every topic's UTM-tagged signup URL"
            >
              <LinkSimple size={16} weight="bold" className="text-[#FF9764]" />
              Copy TikTok bio-link menu
              <span className="text-[10px] text-white/50 font-mono ml-1">
                {reels.length ? `${new Set(reels.map(r => r.topic)).size} topics` : ""}
              </span>
            </button>
            <div className="inline-flex items-center gap-1.5" data-testid="watch-caption-style-picker">
              <span className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Captions</span>
              <button
                type="button"
                onClick={() => pickCapStyle("white")}
                data-testid="watch-caption-style-white"
                className={`rounded-full px-2.5 py-1 text-[10px] font-black transition-colors ${
                  capStyle === "white"
                    ? "bg-white text-black"
                    : "bg-white/10 text-white/60 hover:bg-white/20"
                }`}
              >
                Bold white
              </button>
              <button
                type="button"
                onClick={() => pickCapStyle("orange")}
                data-testid="watch-caption-style-orange"
                className={`rounded-full px-2.5 py-1 text-[10px] font-black transition-colors ${
                  capStyle === "orange"
                    ? "bg-[#FF5A1F] text-black"
                    : "bg-white/10 text-white/60 hover:bg-white/20"
                }`}
              >
                Orange pop
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Champion Wall — real customers first */}
      {!loading && !err && champs.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 pb-8" data-testid="watch-champion-wall">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[11px] uppercase tracking-widest text-[#FF9764] font-bold">
              Real fleets · Champion shout-outs
            </span>
            <span className="text-[10px] font-mono text-white/40">{champs.length}</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {champs.map((r) => renderReelCard(r, "w-44 shrink-0"))}
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="watch-reels-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[9/16] rounded-xl bg-white/5 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && err && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/40 p-4 text-red-300 text-sm" data-testid="watch-reels-error">
            Couldn't load reels: {err}
          </div>
        )}

        {!loading && !err && reels.length === 0 && (
          <div className="rounded-lg bg-white/5 border border-white/10 p-8 text-center text-white/60" data-testid="watch-reels-empty">
            <PlayCircle size={40} weight="duotone" className="mx-auto mb-3 opacity-60" />
            <div className="text-lg font-bold text-white/80">No reels published yet</div>
            <div className="text-xs mt-1">New promo videos will appear here as they get generated.</div>
          </div>
        )}

        {!loading && !err && reels.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="watch-reels-grid">
            {promos.map((reel) => renderReelCard(reel))}
          </div>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-white/10 bg-gradient-to-b from-transparent to-[#FF5A1F]/[0.06]">
        <div className="max-w-3xl mx-auto px-4 py-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-black">Ready to give yourself your weekends back?</h2>
          <p className="text-white/70 mt-2 text-sm">Concierge setup with Mike. Live in 5 business days. 7-day free trial, cancel anytime.</p>
          <Link
            to="/?utm_source=reels_page&utm_medium=organic&utm_campaign=promo_gallery&utm_content=footer"
            data-testid="watch-reels-footer-cta"
            className="mt-5 inline-flex items-center gap-1.5 rounded-md px-5 py-2.5 text-sm font-bold bg-[#FF5A1F] hover:bg-[#FF6A35] text-black transition-colors"
          >
            Sign up free <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
